// `?demo` — the whole app running with no Giraffe attached. One boolean, so
// dev, headless tests and a stakeholder link all exercise the real read path
// (provider -> normalisers -> engine -> grid) rather than a stubbed one.

import { store } from './store';
import { demoData } from '@/data/fixture';
import { createWorkbook, setCellRaw } from '@/model/workbook';
import { parseCellA1 } from '@/model/address';
import type { Sheet, Workbook } from '@/model/types';

export function isDemo(): boolean {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).has('demo');
}

function put(sheet: Sheet, cells: Record<string, string>): void {
  for (const [addr, raw] of Object.entries(cells)) {
    const rc = parseCellA1(addr)!;
    setCellRaw(sheet, rc.row, rc.col, raw);
  }
}

/** A worked example: a usage schedule and a small residual-value proforma. */
export function demoWorkbook(): Workbook {
  const wb = createWorkbook();
  const s = wb.sheets[0]!;
  s.name = 'Feasibility';
  s.colWidths[1] = 150;
  s.colWidths[2] = 110;
  s.colWidths[3] = 110;
  s.colWidths[4] = 130;
  s.colWidths[5] = 130;

  put(s, {
    A1: 'Scheme summary',
    // The unit follows the project rather than being a note that goes stale —
    // switch the toolbar to imperial and this label moves with the number.
    A3: '="Site area ("&IF(GI.UNITS()="imperial","ft²","m²")&")"',
    B3: '=GI.AREA()',
    A4: 'Gross floor area',
    B4: '=GI.TOTAL("grossArea")',
    A5: 'Saleable area',
    B5: '=GI.TOTAL("saleableArea")',
    A6: 'Dwellings',
    B6: '=GI.TOTAL("dwellingTotalCount")',
    A7: 'Plot ratio',
    B7: '=B4/B3',

    A9: 'Schedule by usage',
    A10: 'Usage',
    B10: 'GFA',
    C10: 'Saleable',
    D10: 'Sale rate',
    E10: 'Revenue',

    A11: '=GI.LIST("usage",ROW()-ROW($A$10))',
    B11: '=IF(A11="","",GI.TOTAL("grossArea","usage="&A11))',
    C11: '=IF(A11="","",GI.TOTAL("saleableArea","usage="&A11))',
    D11: '=IF(A11="","",GI.WEIGHTEDAVG("salePrice","usage="&A11))',
    E11: '=IF(A11="","",C11*D11)',

    A16: 'Total',
    B16: '=SUM(B11:B15)',
    C16: '=SUM(C11:C15)',
    E16: '=SUM(E11:E15)',

    A18: 'Residual appraisal',
    A19: 'Gross revenue',
    B19: '=E16',
    A20: 'Construction cost',
    B20: '=-GI.WEIGHTEDAVG("hardCost")*B4',
    A21: 'Fees and contingency',
    B21: '=B20*0.15',
    A22: 'Net before land',
    B22: '=B19+B20+B21',
    A23: 'Target profit @ 20%',
    B23: '=-B19*0.2',
    A24: 'Residual land value',
    B24: '=B22+B23',
  });

  // Fill the schedule rows down from row 11.
  for (let r = 12; r <= 15; r++) {
    put(s, {
      [`A${r}`]: `=GI.LIST("usage",ROW()-ROW($A$10))`,
      [`B${r}`]: `=IF(A${r}="","",GI.TOTAL("grossArea","usage="&A${r}))`,
      [`C${r}`]: `=IF(A${r}="","",GI.TOTAL("saleableArea","usage="&A${r}))`,
      [`D${r}`]: `=IF(A${r}="","",GI.WEIGHTEDAVG("salePrice","usage="&A${r}))`,
      [`E${r}`]: `=IF(A${r}="","",C${r}*D${r})`,
    });
  }

  for (const addr of ['A1', 'A9', 'A18', 'A10', 'B10', 'C10', 'D10', 'E10', 'A16', 'A24']) {
    const rc = parseCellA1(addr)!;
    const key = `${rc.row},${rc.col}`;
    s.cells[key] = { ...s.cells[key], s: { bold: true } };
  }
  for (const addr of ['B3', 'B4', 'B5', 'B16', 'C16']) {
    const rc = parseCellA1(addr)!;
    const key = `${rc.row},${rc.col}`;
    s.cells[key] = { ...s.cells[key], fmt: 'number0' };
  }
  for (let r = 11; r <= 16; r++) {
    for (const c of [2, 3]) s.cells[`${r},${c}`] = { ...s.cells[`${r},${c}`], fmt: 'number0' };
    for (const c of [4, 5]) s.cells[`${r},${c}`] = { ...s.cells[`${r},${c}`], fmt: 'currency0' };
  }
  for (let r = 19; r <= 24; r++) s.cells[`${r},2`] = { ...s.cells[`${r},2`], fmt: 'currency0' };
  s.cells['6,2'] = { ...s.cells['6,2'], fmt: 'number0' };
  s.cells['7,2'] = { ...s.cells['7,2'], fmt: 'number2' };

  return wb;
}

export function startDemo(): void {
  store.setDataProvider(demoData());
  store.loadWorkbook(demoWorkbook());
}
